import React, { useEffect, useState } from 'react';
import { X, Bell, BellOff, MapPin, Loader2, Check, Send } from 'lucide-react';
import { notificationsApi, NotificationPrefs } from '../services/notifications';
import { GeoLocation } from '../types';
import { PostCategory } from '../services/postsApi';

const CATEGORIES: Array<[PostCategory, string]> = [
  ['police_activity', 'Police Activity'],
  ['shooting',        'Shooting / Gunfire'],
  ['fire_ems',        'Fire / EMS'],
  ['traffic',         'Traffic'],
  ['pursuit',         'Pursuit'],
  ['containment',     'Containment'],
  ['suspicious',      'Suspicious'],
  ['community',       'Community'],
  ['other',           'Other'],
];

interface Props {
  open: boolean;
  onClose: () => void;
  userLocation?: GeoLocation | null;
}

const NotificationSettings: React.FC<Props> = ({ open, onClose, userLocation }) => {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(null); setInfo(null);
    notificationsApi.prefs()
      .then(setPrefs)
      .catch(e => setError(e?.message))
      .finally(() => setLoading(false));
  }, [open]);

  const browserOK   = notificationsApi.supported();
  const browserPerm = notificationsApi.permission();
  const subscribed  = (prefs?.subscriptions?.length || 0) > 0;

  const enable = async () => {
    setError(null); setInfo(null); setLoading(true);
    try {
      await notificationsApi.subscribe();
      // Default radius/location/categories on first subscribe
      const next: any = { enabled: true };
      if (!prefs?.location && userLocation) next.location = userLocation;
      await notificationsApi.setPrefs(next);
      setPrefs(await notificationsApi.prefs());
      setInfo('Notifications enabled.');
    } catch (e: any) {
      setError(e?.message || 'Failed to enable.');
    } finally { setLoading(false); }
  };
  const disable = async () => {
    setError(null); setInfo(null); setLoading(true);
    try {
      await notificationsApi.unsubscribe();
      await notificationsApi.setPrefs({ enabled: false });
      setPrefs(await notificationsApi.prefs());
      setInfo('Notifications turned off.');
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  const useCurrentLocation = async () => {
    if (!navigator.geolocation) return setError('Geolocation unavailable.');
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        await notificationsApi.setPrefs({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } as any });
        setPrefs(await notificationsApi.prefs());
        setInfo('Location updated.');
      } catch (e: any) { setError(e?.message); }
      finally { setLoading(false); }
    }, err => { setError(err.message); setLoading(false); });
  };
  const setRadius = async (r: number) => {
    setLoading(true);
    try { await notificationsApi.setPrefs({ radiusMiles: r }); setPrefs(p => p ? { ...p, radiusMiles: r } : p); }
    catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  const toggleCategory = async (c: PostCategory) => {
    if (!prefs) return;
    const next = prefs.categories.includes(c)
      ? prefs.categories.filter(x => x !== c)
      : [...prefs.categories, c];
    setPrefs({ ...prefs, categories: next });
    try { await notificationsApi.setPrefs({ categories: next }); }
    catch (e: any) { setError(e?.message); }
  };
  const sendTest = async () => {
    setError(null); setInfo(null);
    try {
      const r = await notificationsApi.test();
      setInfo(`Test sent — ${r.sent} delivered.`);
    } catch (e: any) { setError(e?.message); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-amber-700/40 rounded-lg w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="text-amber-200 font-bold tracking-wide flex items-center gap-2"><Bell size={16}/> NOTIFICATIONS</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Close"><X size={18}/></button>
        </header>

        <div className="p-4 space-y-3 text-sm">
          {!browserOK && <Banner color="red">This browser doesn't support push notifications.</Banner>}
          {browserOK && browserPerm === 'denied' && <Banner color="amber">Notifications are blocked at the browser level — re-enable in your browser settings, then return here.</Banner>}
          {error && <Banner color="red">{error}</Banner>}
          {info  && <Banner color="emerald">{info}</Banner>}

          {loading && !prefs && <div className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin"/> loading…</div>}

          {prefs && (
            <>
              <div className="flex items-center justify-between border border-slate-800 rounded p-3">
                <div>
                  <div className="font-bold">Push notifications</div>
                  <div className="text-slate-500 text-xs">{subscribed ? 'Subscribed on this device.' : 'Not subscribed.'}</div>
                </div>
                {subscribed
                  ? <button onClick={disable} disabled={loading} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold inline-flex items-center gap-1"><BellOff size={12}/> Turn off</button>
                  : <button onClick={enable}  disabled={loading || !browserOK || browserPerm === 'denied'} className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-slate-950 text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1"><Bell size={12}/> Enable</button>}
              </div>

              <div className="border border-slate-800 rounded p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-bold">Radius — {prefs.radiusMiles.toFixed(1)} mi</div>
                  <span className="text-slate-500 text-xs">around your set location</span>
                </div>
                <input type="range" min="0.25" max="10" step="0.25" value={prefs.radiusMiles}
                  onChange={e => setPrefs({ ...prefs, radiusMiles: parseFloat(e.target.value) })}
                  onMouseUp={() => setRadius(prefs.radiusMiles)}
                  onTouchEnd={() => setRadius(prefs.radiusMiles)}
                  className="w-full"/>
                <div className="flex items-center gap-2">
                  <MapPin size={12} className="text-slate-500"/>
                  <span className="text-xs text-slate-400 flex-1 truncate">
                    {prefs.location ? `${prefs.location.lat.toFixed(4)}, ${prefs.location.lng.toFixed(4)}` : 'No location set'}
                  </span>
                  <button onClick={useCurrentLocation} disabled={loading} className="text-cyan-300 hover:text-cyan-200 text-xs underline">Use current</button>
                </div>
              </div>

              <div className="border border-slate-800 rounded p-3">
                <div className="font-bold mb-2">Categories</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {CATEGORIES.map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-slate-300 cursor-pointer hover:text-slate-100">
                      <input type="checkbox"
                        checked={prefs.categories.length === 0 || prefs.categories.includes(v)}
                        onChange={() => toggleCategory(v)} />
                      <span>{l}</span>
                    </label>
                  ))}
                </div>
                <div className="text-slate-500 text-[10px] mt-2">No selection = all categories.</div>
              </div>

              <div className="border border-slate-800 rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-bold">Daily email digest</div>
                  <div className="text-slate-500 text-xs">Summary of nearby activity, sent once a day.</div>
                </div>
                <input type="checkbox" checked={prefs.digestEmail}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setPrefs({ ...prefs, digestEmail: v });
                    try { await notificationsApi.setPrefs({ digestEmail: v }); } catch (er: any) { setError(er?.message); }
                  }}/>
              </div>

              {subscribed && (
                <button onClick={sendTest} className="w-full px-3 py-2 rounded bg-cyan-700/30 hover:bg-cyan-700/50 text-cyan-200 border border-cyan-600/40 text-xs font-bold inline-flex items-center justify-center gap-1">
                  <Send size={12}/> Send a test notification
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Banner: React.FC<{ color: 'red' | 'amber' | 'emerald'; children: React.ReactNode }> = ({ color, children }) => {
  const map = {
    red:     'bg-red-900/30 border-red-800 text-red-200',
    amber:   'bg-amber-900/30 border-amber-800 text-amber-200',
    emerald: 'bg-emerald-900/30 border-emerald-800 text-emerald-200',
  };
  return <div className={`text-xs border rounded px-2 py-1 ${map[color]}`}>{children}</div>;
};

export default NotificationSettings;
